import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Bug, Edit2, Save, X, Trash2, Plus, ScrollText, Image as ImageIcon } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { EntityImageGallery } from "@/components/EntityImageGallery";
import { authFetch } from "@/lib/auth";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import type { Animal, Pasture, LogEntry, Species } from "@shared/types";

export function AnimalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [animal, setAnimal] = useState<Animal | null>(null);
  const [pastures, setPastures] = useState<Pasture[]>([]);
  const [species, setSpecies] = useState<Species[]>([]);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [eName, setEName] = useState("");
  const [eSpeciesId, setESpeciesId] = useState<string>("");
  const [eBreed, setEBreed] = useState("");
  const [eTag, setETag] = useState("");
  const [eDob, setEDob] = useState("");
  const [ePasture, setEPasture] = useState("");
  const [eStatus, setEStatus] = useState("active");
  const [eNotes, setENotes] = useState("");
  const [logDesc, setLogDesc] = useState("");
  const [logDate, setLogDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [logType, setLogType] = useState("vet");
  const [addingLog, setAddingLog] = useState(false);
  const [showLogForm, setShowLogForm] = useState(false);

  const loadData = useCallback(async () => {
    if (!id) return;
    try {
      const [a, pData, sData, lData] = await Promise.all([
        authFetch<Animal>(`/api/animals/${id}`),
        authFetch<{ items: Pasture[] }>("/api/pastures"),
        authFetch<{ items: Species[] }>("/api/species"),
        authFetch<{ items: LogEntry[] }>("/api/logs"),
      ]);
      setAnimal(a);
      setPastures(pData.items ?? []);
      setSpecies(sData.items ?? []);
      setLogs((lData.items ?? []).filter((l) => l.entity_id === id).sort((a, b) => b.date.localeCompare(a.date)));
      setEName(a.name);
      setESpeciesId(a.species_id ?? "");
      setEBreed(a.breed ?? "");
      setETag(a.tag_number ?? "");
      setEDob(a.dob ?? "");
      setEPasture(a.pasture_id ?? "");
      setEStatus(a.status);
      setENotes(a.notes ?? "");
    } catch (err: any) {
      setError(err?.message || "Failed to load");
      console.error("[AnimalDetail]", err);
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => { loadData(); }, [loadData]);

  const pastureName = pastures.find((p) => p.id === animal?.pasture_id)?.name || "—";
  const speciesName = animal?.species_id
    ? species.find((s) => s.id === animal.species_id)?.name ?? "Unspecified"
    : "Unspecified";

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const updated = await authFetch<Animal>(`/api/animals/${id}`, {
        method: "PATCH",
        body: JSON.stringify({
          name: eName.trim(),
          species_id: eSpeciesId || null,
          breed: eBreed.trim() || null,
          tag_number: eTag.trim() || null,
          dob: eDob || null,
          pasture_id: ePasture || null,
          status: eStatus,
          notes: eNotes.trim() || null,
        }),
      });
      setAnimal(updated);
      setEditing(false);
      toast.success("Animal updated");
    } catch (err: any) {
      toast.error(err?.message || "Failed to update");
    } finally {
      setSaving(false);
    }
  };
  const handleArchive = async () => {
    if (!id) return;
    try {
      await authFetch(`/api/animals/${id}`, { method: "DELETE" });
      toast.success("Animal archived");
      navigate("/animals");
    } catch (err: any) {
      toast.error(err?.message || "Failed to archive");
    }
  };
  const handleAddLog = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!logDesc.trim() || !animal) return;
    setAddingLog(true);
    try {
      await authFetch("/api/logs", {
        method: "POST",
        body: JSON.stringify({
          log_type: logType, entity_type: "animal", entity_id: id,
          entity_name: animal.name, description: logDesc.trim(), date: logDate, metadata: {},
        }),
      });
      toast.success("Log added");
      setLogDesc(""); setShowLogForm(false);
      const lData = await authFetch<{ items: LogEntry[] }>("/api/logs");
      setLogs((lData.items ?? []).filter((l) => l.entity_id === id).sort((a, b) => b.date.localeCompare(a.date)));
    } catch (err: any) {
      toast.error(err?.message || "Failed to add log");
    } finally {
      setAddingLog(false);
    }
  };
  if (loading) {
    return (
      <>
        <PageHeader title="" breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Animals", href: "/animals" }, { label: "Loading..." }]} />
        <div className="space-y-4"><Skeleton className="h-48 w-full rounded-lg" /></div>
      </>
    );
  }
  if (error || !animal) {
    return (
      <>
        <PageHeader title="Animal Not Found" breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Animals", href: "/animals" }, { label: "Error" }]} />
        <div className="p-4 rounded-lg bg-destructive/10 text-destructive text-sm">{error || "Not found"}</div>
      </>
    );
  }
  return (
    <>
      <PageHeader title={animal.name} breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Animals", href: "/animals" }, { label: animal.name }]}>
        {!editing ? (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}><Edit2 className="h-4 w-4 mr-1" /> Edit</Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-destructive border-destructive/30"><Trash2 className="h-4 w-4 mr-1" /> Archive</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>Archive {animal.name}?</AlertDialogTitle><AlertDialogDescription>This will hide the animal from active lists.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleArchive} className="bg-destructive text-destructive-foreground">Archive</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        ) : (
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={saving} className="bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90"><Save className="h-4 w-4 mr-1" /> {saving ? "Saving..." : "Save"}</Button>
            <Button variant="outline" size="sm" onClick={() => setEditing(false)}><X className="h-4 w-4 mr-1" /> Cancel</Button>
          </div>
        )}
      </PageHeader>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2"><Bug className="h-5 w-5 text-ranch-green" /> Animal Profile</CardTitle></CardHeader>
            <CardContent>
              {editing ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Name</Label><Input value={eName} onChange={(e) => setEName(e.target.value)} /></div>
                    <div className="space-y-2"><Label>Species</Label>
                      <Select value={eSpeciesId} onValueChange={setESpeciesId}>
                        <SelectTrigger><SelectValue placeholder="Select species..." /></SelectTrigger>
                        <SelectContent>
                          {species.map((s) => (
                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Breed</Label><Input value={eBreed} onChange={(e) => setEBreed(e.target.value)} /></div>
                    <div className="space-y-2"><Label>Tag #</Label><Input value={eTag} onChange={(e) => setETag(e.target.value)} /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2"><Label>Birth Date</Label><Input type="date" value={eDob} onChange={(e) => setEDob(e.target.value)} /></div>
                    <div className="space-y-2"><Label>Pasture</Label>
                      <Select value={ePasture} onValueChange={setEPasture}><SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent>{pastures.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
                    </div>
                    <div className="space-y-2"><Label>Status</Label>
                      <Select value={eStatus} onValueChange={setEStatus}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="sold">Sold</SelectItem>
                          <SelectItem value="deceased">Deceased</SelectItem>
                          <SelectItem value="retired">Retired</SelectItem>
                          <SelectItem value="quarantined">Quarantined</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2"><Label>Notes</Label><Textarea value={eNotes} onChange={(e) => setENotes(e.target.value)} rows={3} /></div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-sm text-muted-foreground">Species</p><p className="font-medium">{speciesName}</p></div>
                  <div><p className="text-sm text-muted-foreground">Breed</p><p className="font-medium">{animal.breed || "—"}</p></div>
                  <div><p className="text-sm text-muted-foreground">Tag #</p><p className="font-medium">{animal.tag_number || "—"}</p></div>
                  <div><p className="text-sm text-muted-foreground">Status</p><StatusBadge status={animal.status} /></div>
                  <div><p className="text-sm text-muted-foreground">Birth Date</p><p className="font-medium">{animal.dob ? format(parseISO(animal.dob + "T00:00:00"), "MMM d, yyyy") : "—"}</p></div>
                  <div><p className="text-sm text-muted-foreground">Pasture</p><p className="font-medium">{pastureName}</p></div>
                  {animal.notes && <div className="col-span-2"><p className="text-sm text-muted-foreground">Notes</p><p className="text-sm">{animal.notes}</p></div>}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <ImageIcon className="h-4 w-4 text-ranch-green" /> Photos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <EntityImageGallery entityType="animal" entityId={animal.id} title="" />
            </CardContent>
          </Card>
        </div>
        <div>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-base flex items-center gap-2"><ScrollText className="h-4 w-4" /> Health & Care Log</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowLogForm(!showLogForm)}><Plus className="h-4 w-4" /></Button>
            </CardHeader>
            <CardContent>
              {showLogForm && (
                <form onSubmit={handleAddLog} className="mb-4 p-3 rounded-lg bg-muted/50 space-y-3">
                  <Select value={logType} onValueChange={setLogType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="vet">Vet</SelectItem><SelectItem value="animal-care">Animal Care</SelectItem></SelectContent>
                  </Select>
                  <Input type="date" value={logDate} onChange={(e) => setLogDate(e.target.value)} />
                  <Textarea placeholder="What happened?" value={logDesc} onChange={(e) => setLogDesc(e.target.value)} rows={2} />
                  <Button type="submit" size="sm" disabled={addingLog} className="w-full bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90">
                    {addingLog ? "Adding..." : "Add Entry"}
                  </Button>
                </form>
              )}
              {logs.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No health or care logs yet.</p>
              ) : (
                <div className="space-y-3">
                  {logs.map((log) => (
                    <div key={log.id} className="border-l-2 border-ranch-green pl-3 py-1">
                      <div className="flex items-center gap-2">
                        <p className="text-2xs text-muted-foreground">{log.date ? format(parseISO(log.date + "T00:00:00"), "MMM d, yyyy") : ""}</p>
                        <StatusBadge status={log.log_type} />
                      </div>
                      <p className="text-sm text-foreground mt-0.5">{log.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
